import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  CircularProgress,
  Button,
  Chip,
  IconButton,
  CardMedia,
} from '@mui/material';
import { ArrowBack, Favorite, FavoriteBorder, LocationOn, Event } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { usePortal } from './PortalContext';
import api from '../services/api';

const PortalSaleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = usePortal();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState({});

  useEffect(() => {
    fetchSaleDetail();
    if (isAuthenticated) {
      fetchWishlist();
    }
  }, [id]);

  const fetchSaleDetail = async () => {
    try {
      const response = await api.get(`/portal/sales/${id}`);
      setSale(response.data.sale);
    } catch (error) {
      console.error('Failed to fetch sale detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWishlist = async () => {
    try {
      const token = localStorage.getItem('portal_access_token');
      const response = await api.get('/portal/wishlist', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const wishlistMap = {};
      response.data.wishlist.forEach((item) => {
        wishlistMap[item.item_id] = true;
      });
      setWishlist(wishlistMap);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    }
  };

  const toggleWishlist = async (itemId) => {
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }

    try {
      const token = localStorage.getItem('portal_access_token');

      if (wishlist[itemId]) {
        await api.delete(`/portal/wishlist/${itemId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlist((prev) => ({ ...prev, [itemId]: false }));
      } else {
        await api.post('/portal/wishlist', { item_id: itemId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlist((prev) => ({ ...prev, [itemId]: true }));
      }
    } catch (error) {
      console.error('Wishlist error:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!sale) {
    return <Typography>Sale not found</Typography>;
  }

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/portal/sales')} sx={{ mb: 2 }}>
        Back to Sales
      </Button>

      <Card sx={{ mb: 3 }}>
        {sale.photos?.length > 0 && (
          <CardMedia
            component="img"
            height="400"
            image={`${process.env.REACT_APP_API_URL || 'https://api.paceestate.com'}${sale.photos[0].file_path}`}
            alt={sale.name}
          />
        )}
        <CardContent>
          <Typography variant="h4" gutterBottom>
            {sale.name}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip
              icon={<LocationOn />}
              label={`${sale.address}, ${sale.city}, ${sale.state} ${sale.zip_code}`}
            />
            <Chip
              icon={<Event />}
              label={`${new Date(sale.start_date).toLocaleDateString()} - ${new Date(sale.end_date).toLocaleDateString()}`}
            />
          </Box>

          <Typography variant="body1" sx={{ mt: 2 }}>
            {sale.description}
          </Typography>

          {sale.sale_days && sale.sale_days.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Sale Hours
              </Typography>
              {sale.sale_days.map((day, index) => (
                <Typography key={index} variant="body2">
                  {new Date(day.date).toLocaleDateString()}: {day.start_time} - {day.end_time}
                </Typography>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Typography variant="h5" gutterBottom>
        Items for Sale
      </Typography>

      <Grid container spacing={2}>
        {sale.items?.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.id}>
            <Card>
              {item.photos?.[0] && (
                <CardMedia
                  component="img"
                  height="200"
                  image={`${process.env.REACT_APP_API_URL || 'https://api.paceestate.com'}${item.photos[0].file_path}`}
                  alt={item.name}
                />
              )}
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">{item.name}</Typography>
                    <Typography variant="h5" color="primary" sx={{ mt: 1 }}>
                      ${item.price}
                    </Typography>
                    <Chip
                      label={item.status}
                      color={item.status === 'available' ? 'success' : 'default'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  {isAuthenticated && (
                    <IconButton
                      onClick={() => toggleWishlist(item.id)}
                      color="error"
                    >
                      {wishlist[item.id] ? <Favorite /> : <FavoriteBorder />}
                    </IconButton>
                  )}
                </Box>
                {item.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {item.description.substring(0, 100)}...
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {(!sale.items || sale.items.length === 0) && (
        <Typography color="text.secondary">
          Items coming soon...
        </Typography>
      )}
    </Box>
  );
};

export default PortalSaleDetail;
